import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { Select } from '../../components/ui/form';
import { TextInput } from '../../components/ui/form/TextInput';
import { api } from '../../lib/api';
import { useDocumentTitle } from '../../lib/title';
import type { TrackSummary } from '../tracks/TrackList';
import { Verified } from '../../components/ui/Verified';

interface Current {
  id: number;
  title: string;
  artwork_small: string | null;
  owner_username: string;
  owner_display_name: string | null; verified?: boolean;
  ends_at: string | null;
}

interface Past {
  id: number;
  track_id: number;
  title: string;
  artwork: string | null;
  owner_username: string;
  owner_display_name: string | null; verified?: boolean;
  ends_at: string | null;
  created_at: string;
  chosen_by: string | null;
}

/**
 * Choosing the track the dashboard leads with.
 *
 * ## An override, not a setting
 *
 * The hero already picks itself, by recent signal against a low follower count,
 * and that is the right default: it surfaces things nobody has heard yet
 * without anybody curating. This screen exists for the weeks when somebody
 * knows better - a release worth leading with, an artist having a moment.
 *
 * Which is why "Stop featuring" is a first-class button rather than a
 * confirmation dialog. Taking it down is not undoing a mistake, it is the
 * normal end of the thing, and the page goes back to choosing on its own.
 *
 * ## It says what happens when it expires
 *
 * The one thing somebody setting an end time needs to know is what is there
 * afterwards, and "the page picks again" is not guessable from a form with a
 * duration in it. So the form says it.
 */
export function AdminFeaturedPage() {
  useDocumentTitle('Featured');

  const queryClient = useQueryClient();

  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<TrackSummary | null>(null);
  const [hours, setHours] = useState('168');

  const { data, isPending } = useQuery({
    queryKey: ['admin', 'featured'],
    queryFn: () => api.get<{ current: Current | null; history: Past[] }>('/admin/featured'),
  });

  /*
   * The same search everybody else uses, rather than a picker of its own.
   *
   * It already knows how to rank a title against an artist name, and a second
   * one here would be a second set of results to explain when they disagree.
   * Two characters is where that endpoint starts answering.
   */
  const results = useQuery({
    queryKey: ['search', 'tracks', query],
    queryFn: () => api.get<{ tracks: TrackSummary[] }>(`/search?q=${encodeURIComponent(query)}&type=tracks`),
    enabled: query.trim().length >= 2,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'featured'] });
    // The dashboard is what this screen is about. Without it, an admin who
    // features something and goes home sees the old hero until a reload.
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const feature = useMutation({
    mutationFn: (track: TrackSummary) =>
      api.post('/admin/featured', { track_id: track.id, hours: Number(hours) }),
    onSuccess: () => {
      setChosen(null);
      setQuery('');
      refresh();
    },
  });

  const stop = useMutation({
    mutationFn: () => api.delete('/admin/featured'),
    onSuccess: refresh,
  });

  const current = data?.current ?? null;
  const found = results.data?.tracks ?? [];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Featured</h1>
        <p className="max-w-2xl text-sm leading-relaxed text-soft">
          The track at the top of the signed-in home page. Leave it alone and the page picks its
          own. Something with recent saves and few followers, from the last month.
        </p>
      </header>

      {/* ── What is up there now ──────────────────────────────────────── */}
      <Card className="flex flex-col gap-4 p-5">
        <h2 className="meta-caps text-faint">On the dashboard now</h2>

        {isPending ? (
          <SkeletonRow />
        ) : current === null ? (
          <p className="text-sm text-soft">
            Nothing chosen. The page is picking for itself.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-4" data-featured-current>
            {current.artwork_small !== null && (
              <img
                src={current.artwork_small}
                alt=""
                className="h-14 w-14 shrink-0 rounded-[var(--radius-chip)] object-cover"
              />
            )}

            <div className="flex min-w-0 flex-col gap-0.5">
              <Link to={`/tracks/${current.id}`} className="text-sm font-medium text-ink hover:underline">
                {current.title}
              </Link>
              <span className="meta-caps text-faint">
                {current.owner_display_name ?? `@${current.owner_username}`}
              </span>
            </div>

            <span className="meta text-faint">
              {current.ends_at === null
                ? 'until you stop it'
                : `until ${new Date(current.ends_at).toLocaleString()}`}
            </span>

            <Button
              variant="secondary"
              className="ml-auto"
              data-featured-stop
              loading={stop.isPending}
              onClick={() => stop.mutate()}
            >
              Stop featuring
            </Button>
          </div>
        )}
      </Card>

      {/* ── Choosing one ──────────────────────────────────────────────── */}
      <Card className="flex flex-col gap-4 p-5">
        <h2 className="meta-caps text-faint">Feature a track</h2>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex min-w-[16rem] flex-1 flex-col gap-1.5">
            <label htmlFor="featured-search" className="text-sm font-medium text-soft">
              Find it
            </label>
            <TextInput
              id="featured-search"
              value={query}
              placeholder="Title or artist"
              data-featured-search
              onChange={(event) => {
                setQuery(event.target.value);
                setChosen(null);
              }}
            />
          </div>

          <div className="flex w-48 flex-col gap-1.5">
            <label htmlFor="featured-hours" className="text-sm font-medium text-soft">
              For how long
            </label>
            <Select
              id="featured-hours"
              value={hours}
              data-featured-hours
              options={[
                { value: '24', label: 'A day' },
                { value: '72', label: 'Three days' },
                { value: '168', label: 'A week' },
                { value: '336', label: 'Two weeks' },
                { value: '1008', label: 'Six weeks' },
                { value: '0', label: 'Until I stop it' },
              ]}
              onChange={setHours}
            />
          </div>
        </div>

        {/*
          Said under the form rather than left to be discovered.

          The one thing somebody setting an end time needs to know is what is
          there afterwards, and a duration field does not imply it.
        */}
        <p className="text-xs text-faint">
          When it ends, the page goes back to picking its own. Choosing a new one replaces whatever
          is up there now.
        </p>

        {query.trim().length >= 2 && (
          <ul className="flex flex-col gap-1 border-t border-line pt-3" data-featured-results>
            {results.isPending && <SkeletonRow />}

            {results.data !== undefined && found.length === 0 && (
              <li className="text-sm text-soft">Nothing matches that.</li>
            )}

            {found.map((track) => (
              <li key={track.id}>
                <button
                  type="button"
                  data-featured-pick={track.id}
                  onClick={() => setChosen(track)}
                  className={`flex w-full items-center gap-3 rounded-[var(--radius-control)] p-2 text-left transition-colors ${
                    chosen?.id === track.id ? 'bg-raised' : 'hover:bg-raised'
                  }`}
                >
                  {track.artwork_small !== null && (
                    <img
                      src={track.artwork_small}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded-[var(--radius-chip)] object-cover"
                    />
                  )}

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ink">{track.title}</span>
                    <span className="block truncate text-[11px] text-faint">
                      {track.owner?.display_name ?? `@${track.owner?.username ?? ''}`}
                      {track.owner?.verified && <Verified />}
                    </span>
                  </span>

                  {chosen?.id === track.id && <Chip tone="signal">Chosen</Chip>}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center gap-3 border-t border-line pt-4">
          <Button
            disabled={chosen === null}
            loading={feature.isPending}
            data-featured-save
            onClick={() => chosen !== null && feature.mutate(chosen)}
          >
            {chosen === null ? 'Pick a track' : `Feature ${chosen.title}`}
          </Button>

          {feature.isError && <span className="meta text-negative">That did not save.</span>}
        </div>
      </Card>

      {/* ── What has been up there ────────────────────────────────────── */}
      {(data?.history.length ?? 0) > 0 && (
        <Card className="flex flex-col gap-3 p-5">
          <h2 className="meta-caps text-faint">Before this</h2>

          <ul className="flex flex-col gap-2" data-featured-history>
            {data?.history.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center gap-3">
                {entry.artwork !== null && (
                  <img
                    src={entry.artwork}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded-[var(--radius-chip)] object-cover"
                  />
                )}

                <Link to={`/tracks/${entry.track_id}`} className="text-sm text-ink hover:underline">
                  {entry.title}
                </Link>

                <span className="meta-caps text-faint">
                  {entry.owner_display_name ?? `@${entry.owner_username}`}
                </span>

                <span className="meta ml-auto text-ghost">
                  {new Date(entry.created_at).toLocaleDateString()}
                  {entry.chosen_by !== null && ` · ${entry.chosen_by}`}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
